import './polyfill.ts';

import {html, LitElement} from 'lit';
import {state, customElement, query} from 'lit/decorators.js';
import {routes} from './router.ts';
import {Router} from '@vaadin/router';
import {initializeAnalytics} from './analytics.ts';
import {finishAuthenticationProcedure} from './initialization/sso.ts';
import {initializeViewHeight} from './vhHack.ts';
import {languageCodes} from './i18n.ts';
import {defaultLanguage} from './unspecific/languageUtils.ts';
import {normalizeURL, PARTIAL} from './urlHandling/urlNormalizer.ts';
import './elements/element-header.ts';
import './elements/element-top-notification.ts';
import type {Ref} from 'lit/directives/ref.js';
import {ref, createRef} from 'lit/directives/ref.js';
import {getBrowserCode} from './helpers/dom.ts';
import {setCanonicalAndLanguageRelatedMetadata} from './initialization/canonicalURLHandler.ts';
import {planLazyLoadingMapRelatedComponents} from './initialization/mapComponentsLazyLoading.ts';
import {registerModalObserver} from './initialization/modalObserver.ts';
import {loadFooter} from './initialization/footerLoader.ts';
import './elements/element-notifications.ts';
import type ElementNotifications from './elements/element-notifications.ts';
import {resolveDeployPrefix} from './initialization/deployPrefixResolver.ts';
import type {AdminMessage} from './apis/api-common.ts';
import {
  fetchAdminMessage,
  updateMaintenanceStatus,
  fetchSeason,
} from './apis/api-common.ts';
import {
  adminMessageLoaded$,
  adminMessageShown$,
  filterDirty$,
  modal$,
  season$,
} from './stores/store-main.ts';
import {classMap} from 'lit/directives/class-map.js';
import {isFilterDirty} from './helpers/filter.ts';
import './elements/overlays/element-overlay.ts';
import {resolvePartialUrl} from './initialization/partialURLHandler.ts';
import {
  initializeObsoleteApiWatchdog,
  needsReload$,
} from './initialization/obsoleteApi.ts';
import './elements/element-bottom-sheet.ts';
import {initializeUserWithToken} from './helpers/keycloakUser.ts';
import {msg} from '@lit/localize';
import {initializeUserWithCookie} from './helpers/cookieUser.ts';
import {hasAuthCookie} from './helpers/tokenOrCookie.ts';
import {toursFeedbackParamCheck} from './urlHandling/urlSearchParams.ts';

main();

async function main() {
  initializeObsoleteApiWatchdog();
  initializeAnalytics();
  finishAuthenticationProcedure();

  const deployPrefix = resolveDeployPrefix();

  // I18n and URL normalization should be first
  // WARNING: URL coordinates reprojection is deferred to map creation.
  const initialURL = document.location.toString();
  const defaultSeasonCode = await fetchSeason();
  season$.next(defaultSeasonCode);
  const chosenFilters = localStorage.getItem('filters');
  filterDirty$.next(
    chosenFilters !== null ? isFilterDirty(JSON.parse(chosenFilters)) : false,
  );
  const {normalizedURL, detectedLang, canonical, bag, code} = normalizeURL(
    initialURL,
    defaultLanguage,
    defaultSeasonCode,
    deployPrefix,
  );
  if (normalizedURL && normalizedURL !== initialURL) {
    window.history.replaceState(null, undefined, normalizedURL);
  }

  if (canonical) {
    setCanonicalAndLanguageRelatedMetadata(
      normalizedURL,
      detectedLang,
      languageCodes,
      defaultSeasonCode,
      document,
    );
  } else if (code === PARTIAL) {
    const url = await resolvePartialUrl(bag, normalizedURL);
    if (url) {
      location.href = url.toString();
      return;
    }
  }
  // Then go on with the rest of the initialization
  initializeViewHeight();
  if (hasAuthCookie()) {
    await initializeUserWithCookie();
  } else {
    await initializeUserWithToken();
  }
  await updateMaintenanceStatus();

  const appIndexElement = document.createElement('app-index');
  const appEmbedIndexElement = document.createElement('app-embed-index');
  document.body.appendChild(bag.embed ? appEmbedIndexElement : appIndexElement);
}

// TODO
// Unfortunately the (full) URL initialization will actually be asynchronous:
// 1- resolution of internal WebDB ids: /fr/cycling-in-switzerland/etappe-0859.html -> ??
// 2- addition of a slug: /it/svizzera-in-bici/percorsi9/tappa1 -> /it/svizzera-in-bici/percorsi9-????/tappa1-yyy

// For 2 we can do a lazy enhancement when the json with the segment data is retrieved
// but for 1 there is no such solution? Or maybe create a trampoline page (non-canonical)
// for performance we can also expose a dedicated backend endpoint

/**
 * The entrypoints of the application.
 * The app-index is the main entrypoint for the application.
 * The app-embed-index is the entrypoint for the embeddable version (iFrame) of the application.
 */

// end of S sizes (for design system)
@customElement('app-embed-index')
// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
class AppEmbedIndex extends LitElement {
  outletRef: Ref<HTMLElement> = createRef();

  constructor() {
    super();
    planLazyLoadingMapRelatedComponents();
  }

  override render() {
    return html`
      <div
        id="main"
        class="overflow-overlay snap-y snap-proximity overflow-x-hidden"
      >
        <div ${ref(this.outletRef)}></div>
      </div>
    `;
  }

  override async firstUpdated() {
    const router = new Router();
    router.setOutlet(this.outletRef.value);
    router.setRoutes(routes);
    registerModalObserver();
  }

  override createRenderRoot() {
    return this;
  }
}

@customElement('app-index')
// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
class AppIndex extends LitElement {
  @state() adminMessage: AdminMessage | null;
  @state() adminMessageShown = false;
  @query('#main') scrollContainer: HTMLElement;
  @query('element-top-notification') topNotificationEl: HTMLElement;
  outletRef: Ref<HTMLElement> = createRef();
  notifRef: Ref<ElementNotifications> = createRef();
  showToursFeedback = toursFeedbackParamCheck();

  constructor() {
    super();
    planLazyLoadingMapRelatedComponents();
  }

  override render() {
    return html`
      <element-top-notification
        .message="${this.adminMessage}"
      ></element-top-notification>
      <element-header class="sticky top-0 z-[60] block"></element-header>
      <div
        id="main"
        class="overflow-overlay ${classMap({
          'h-screen-wo-header': !this.adminMessageShown,
          'h-screen-wo-header-message': this.adminMessageShown,
        })} snap-y snap-proximity overflow-x-hidden"
      >
        <div
          .hidden="${getBrowserCode() !== 'firefox'}"
          class="h-[0.01px] snap-start"
        ></div>
        <div ${ref(this.outletRef)}></div>
        <element-footer></element-footer>
        <div
          .hidden="${getBrowserCode() !== 'firefox'}"
          class="h-[1px] snap-end"
        ></div>
      </div>
      <element-place-details></element-place-details>
      <element-sightseeing-details></element-sightseeing-details>
      <element-travelreport-details></element-travelreport-details>
      <element-layers></element-layers>
      <element-poi-popup></element-poi-popup>
      <element-big-gallery></element-big-gallery>
      <element-overlay></element-overlay>
      <div
        class="notification-position w-notification flex justify-center fixed bottom-[20px] z-[70]"
      >
        <element-notifications ${ref(this.notifRef)}></element-notifications>
      </div>
      <element-bottom-sheet
        class="absolute bottom-0 w-full pointer-events-none"
        .scrollContainer="${this.scrollContainer}"
        .topNotificationEl="${this.topNotificationEl}"
      ></element-bottom-sheet>
    `;
  }

  override async firstUpdated() {
    const router = new Router();
    router.setOutlet(this.outletRef.value);
    router.setRoutes(routes);
    loadFooter(this.outletRef.value!);
    registerModalObserver();

    this.addEventListener('alertnotif', ((evt: CustomEvent) => {
      const notification = {
        type: evt.detail.type,
        message: evt.detail.message,
        html: evt.detail.html,
      };
      this.notifRef.value!.sendNotification(notification);
    }) as EventListener);

    // no need to unsubscribe in this component
    needsReload$.subscribe((state) => {
      if (state.reload) {
        this.notifRef.value!.sendNotification(
          {
            type: 'error',
            html: html`<div @click=${() => document.location.reload()}>
              ${msg('An error occurred, please reload the page')}
            </div>`,
            message: '.',
          },
          10000000000000,
        );
      }
    });
    const message = await fetchAdminMessage();
    this.adminMessage = message;
    adminMessageShown$.next(!!message);
    adminMessageShown$.subscribe((show) => (this.adminMessageShown = show));
    adminMessageLoaded$.next(true);

    if (this.showToursFeedback) {
      modal$.next({type: 'contact'});
    }
  }

  override createRenderRoot() {
    return this;
  }
}

// Declare our window.envs property
// To allow minification, we want it here instead of the index.html file.
declare global {
  interface Window {
    envs: {
      BASE_API_URL: string;
      ROUTE_LOGOS_BASE_URL: string;
      BASE_DATA_URL: string;
      POI_BASE_URL: string;
      WEBDB_S3_URL: string;
      GA_MEASUREMENT_ID: string;
      IMAGE_BASE_URL: string;
      CAPTCHA_SITEKEY: string;
    };
  }
}
